import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BottomTabBar from "@/components/BottomTabBar";

const Landing = lazy(() => import("@/pages/Landing"));
const Auth = lazy(() => import("@/pages/Auth"));
const Feed = lazy(() => import("@/pages/Feed"));
const ArtworkDetail = lazy(() => import("@/pages/ArtworkDetail"));
const Create = lazy(() => import("@/pages/Create"));
const Profile = lazy(() => import("@/pages/Profile"));
const Collections = lazy(() => import("@/pages/Collections"));
const Drafts = lazy(() => import("@/pages/Drafts"));
const Discussion = lazy(() => import("@/pages/Discussion"));
const Messages = lazy(() => import("@/pages/Messages"));
const Admin = lazy(() => import("@/pages/Admin"));

function PageLoader() {
  return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Loading…</div>;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grain relative flex flex-col">
      <Navbar />
      <main className="relative z-[2] flex-1 pb-24 md:pb-0">
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </main>
      <Footer />
      <BottomTabBar />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/" element={<Shell><Landing /></Shell>} />
          <Route path="/auth" element={<Shell><Auth /></Shell>} />
          <Route path="/feed" element={<Shell><Feed /></Shell>} />
          <Route path="/artwork/:id" element={<Shell><ArtworkDetail /></Shell>} />
          <Route path="/create" element={<Shell><Protected><Create /></Protected></Shell>} />
          <Route path="/edit/:id" element={<Shell><Protected><Create /></Protected></Shell>} />
          <Route path="/collections" element={<Shell><Protected><Collections /></Protected></Shell>} />
          <Route path="/drafts" element={<Shell><Protected><Drafts /></Protected></Shell>} />
          <Route path="/discuss" element={<Shell><Discussion /></Shell>} />
          <Route path="/messages" element={<Shell><Protected><Messages /></Protected></Shell>} />
          <Route path="/profile/:id" element={<Shell><Profile /></Shell>} />
          <Route path="/admin" element={<Shell><Protected><Admin /></Protected></Shell>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
